import { Cli, z } from "incur";
import { validateApiKey, apiBaseUrl } from "./client";
import { readConfig } from "./config";
import { getSecret } from "./secret-store";
import { OAuthError, listOAuthAccounts, performOAuthLogin } from "./oauth";
import {
	deriveProfileName,
	getActiveProfile,
	getValidAccessToken,
	listProfiles,
	removeAllProfiles,
	removeProfile,
	setActiveProfileAccount,
	switchProfile,
	upsertProfile,
} from "./profiles";
import {
	type AccountChoice,
	chooseAuthMethod,
	PromptCancelledError,
	promptApiKey,
	selectAccount,
	selectProfile,
	startSpinner,
} from "./prompts";
import { AuthMethod, Identity, Profile } from "./types";

/**
 * Auth commands: `whop auth login | logout | switch | account | status | list`.
 *
 * The login/logout business logic is context-free (`performLogin`,
 * `removeProfile`) and the incur run handlers are thin adapters. `loginAdapter`
 * and `logoutAdapter` are shared so the top-level `whop login` / `whop logout`
 * (registered in `index.ts`) behave identically without duplicating logic —
 * incur aliases can't cross from a group into a top-level command.
 */

// --- CTA / error shapes accepted by the run context (kept minimal & structural) ---
type CtaCommand =
	| string
	| {
			command: string;
			description?: string | undefined;
			options?: Record<string, unknown> | undefined;
			args?: Record<string, unknown> | undefined;
	  };
type CtaInput = { description?: string | undefined; commands: CtaCommand[] };
type ErrorInput = {
	code: string;
	message: string;
	cta?: CtaInput | undefined;
	exitCode?: number | undefined;
	retryable?: boolean | undefined;
};

// --- Shared schemas ---
export const loginOptions = z.object({
	apiKey: z
		.string()
		.optional()
		.describe("API key for non-interactive login (skips the prompt)"),
	profile: z
		.string()
		.optional()
		.describe("Name for this profile (defaults to the account route)"),
	accountId: z
		.string()
		.optional()
		.describe(
			"Business account to act on behalf of (biz_xxx) — for OAuth users who manage more than one",
		),
});

export const loginEnv = z.object({
	WHOP_API_KEY: z
		.string()
		.optional()
		.describe("API key for non-interactive login"),
	WHOP_API_BASE_URL: z
		.string()
		.optional()
		.describe("Override the Whop API base URL"),
	WHOP_CONFIG_DIR: z
		.string()
		.optional()
		.describe("Override the CLI config directory"),
});

export const loginOutput = z.object({
	profile: z.string().describe("The profile name"),
	active: z.boolean().describe("Whether this profile is now the active one"),
	method: AuthMethod.describe("The authentication method"),
	identity: Identity.describe("The authenticated account or user identity"),
});
export type LoginOutput = z.infer<typeof loginOutput>;

export const logoutOptions = z.object({
	profile: z
		.string()
		.optional()
		.describe("Profile to remove (defaults to the active profile)"),
	all: z
		.boolean()
		.optional()
		.describe("Remove every profile and its stored secret (full reset)"),
});
export const logoutOutput = z.object({
	removed: z.array(z.string()).describe("The removed profile name(s)"),
	newActive: z
		.string()
		.nullable()
		.describe("The profile that is now active, if any"),
});
export type LogoutOutput = z.infer<typeof logoutOutput>;

const statusOutput = z.object({
	loggedIn: z
		.boolean()
		.describe("Whether the active profile has a usable stored credential"),
	profile: z.string().nullable(),
	method: AuthMethod.nullable(),
	identity: Identity.nullable().describe(
		"The logged-in user (OAuth) or the account (API key)",
	),
	account: Identity.nullable().describe(
		"The account auto-filled for scoped commands (account_id/company_id)",
	),
	hasSecret: z
		.boolean()
		.describe("Whether a secret was found in the OS keychain for this profile"),
});

const listOutput = z.object({
	active: z.string().nullable(),
	profiles: z.array(Profile),
});

const switchArgs = z.object({
	name: z
		.string()
		.optional()
		.describe("Profile to switch to (interactive picker if omitted)"),
});
const switchOutput = z.object({
	active: z.string().describe("The newly active profile"),
});

const accountArgs = z.object({
	id: z
		.string()
		.optional()
		.describe(
			"Business account to make the default — id (biz_xxx) or route (interactive picker if omitted)",
		),
});
const accountOptions = z.object({
	list: z
		.boolean()
		.optional()
		.describe(
			"List the business accounts you can manage without changing the default",
		),
});
const accountListItem = z.object({
	id: z.string(),
	title: z.string(),
	route: z.string(),
	active: z
		.boolean()
		.describe("Whether this is the profile's default business account"),
});
const accountOutput = z.object({
	profile: z.string().describe("The active profile"),
	account: Identity.nullable().describe(
		"The default business account passed to scoped commands",
	),
	accounts: z
		.array(accountListItem)
		.describe("Business accounts this user can manage"),
});

export const loginExamples = [
	{ description: "Interactive login" },
	{
		options: { apiKey: "whop_xxx" },
		description: "Non-interactive login (CI / agents)",
	},
	{
		options: { apiKey: "whop_xxx", profile: "prod" },
		description: "Log in and name the profile",
	},
];
export const loginHint =
	"Agents / CI: pass --api-key or set WHOP_API_KEY to skip the prompt.";

// --- Context-free login flows ---
type LoginResult =
	| { ok: true; output: LoginOutput; existed: boolean; underscoped: boolean }
	| { ok: false; code: string; message: string };

export async function performLogin(input: {
	agent: boolean;
	apiKey?: string | undefined;
	profile?: string | undefined;
	accountId?: string | undefined;
	deferAccountSelection?: boolean | undefined;
}): Promise<LoginResult> {
	let method: AuthMethod = "api_key";
	if (!input.agent && !input.apiKey) method = await chooseAuthMethod();

	// Route to the OAuth browser flow.
	if (method === "oauth") {
		try {
			const { profileName, identity, existed } = await performOAuthLogin({
				profile: input.profile,
				agent: input.agent,
				accountId: input.accountId,
				deferAccountSelection: input.deferAccountSelection,
			});
			return {
				ok: true,
				existed,
				underscoped: false,
				output: {
					profile: profileName,
					active: true,
					method: "oauth",
					identity,
				},
			};
		} catch (err) {
			if (err instanceof OAuthError) {
				return { ok: false, code: err.code, message: err.message };
			}
			throw err;
		}
	}

	// Resolve the API key: flag/env > interactive prompt > error in agent mode.
	let apiKey = input.apiKey;
	if (!apiKey) {
		if (input.agent) {
			return {
				ok: false,
				code: "API_KEY_REQUIRED",
				message: "No API key provided and not running interactively.",
			};
		}
		apiKey = await promptApiKey();
	}
	// Normalize: the SDK adds the `Bearer ` prefix itself, so strip one if pasted.
	const key = apiKey.replace(/^Bearer\s+/i, "").trim();

	// 3. Validate against the API.
	let result: Awaited<ReturnType<typeof validateApiKey>>;
	if (input.agent) {
		result = await validateApiKey(key, apiBaseUrl());
	} else {
		const s = startSpinner("Validating API key…");
		result = await validateApiKey(key, apiBaseUrl());
		const okay = result.status === "valid" || result.status === "underscoped";
		s.stop(okay ? "API key validated." : "API key check failed.");
	}
	if (result.status === "invalid") {
		return {
			ok: false,
			code: "INVALID_API_KEY",
			message: "That API key was rejected (401 Unauthorized).",
		};
	}
	if (result.status === "error") {
		return { ok: false, code: "VALIDATION_FAILED", message: result.message };
	}

	// 4. Persist (secret → keychain, metadata → config) and activate.
	const identity = result.identity;
	const name = input.profile ?? deriveProfileName(identity);
	const existed = listProfiles().some((profile) => profile.name === name);
	const profile = upsertProfile({ name, method, secret: key, identity });

	return {
		ok: true,
		existed,
		underscoped: result.status === "underscoped",
		output: { profile: profile.name, active: true, method, identity },
	};
}

// --- Shared adapters (used by the group command and the top-level alias) ---
type LoginAdapterCtx = {
	agent: boolean;
	options: {
		apiKey?: string | undefined;
		profile?: string | undefined;
		accountId?: string | undefined;
	};
	env: { WHOP_API_KEY?: string | undefined };
	ok: (data: LoginOutput, meta?: { cta?: CtaInput | undefined }) => never;
	error: (options: ErrorInput) => never;
};

export async function loginAdapter(c: LoginAdapterCtx): Promise<never> {
	let result: LoginResult;
	try {
		result = await performLogin({
			agent: c.agent,
			apiKey: c.options.apiKey ?? c.env.WHOP_API_KEY,
			profile: c.options.profile,
			accountId: c.options.accountId,
		});
	} catch (error) {
		if (error instanceof PromptCancelledError) {
			return c.error({
				code: "CANCELLED",
				message: "Login cancelled.",
				exitCode: 130,
			});
		}
		throw error;
	}

	if (!result.ok) {
		let cta: CtaInput | undefined;
		if (result.code === "API_KEY_REQUIRED") {
			cta = {
				description: "Provide a key non-interactively:",
				commands: [
					{
						command: "auth login",
						options: { apiKey: true },
						description: "Pass --api-key <key>",
					},
				],
			};
		} else if (
			result.code === "MULTIPLE_ACCOUNTS" ||
			result.code === "ACCOUNT_NOT_FOUND"
		) {
			cta = {
				description: "Choose which business account to use:",
				commands: [
					{
						command: "auth login",
						options: { accountId: true },
						description: "Pass --account-id biz_xxx",
					},
				],
			};
		}
		return c.error({
			code: result.code,
			message: result.message,
			retryable: result.code !== "INVALID_API_KEY",
			cta,
		});
	}

	const accountId = getActiveProfile()?.accountId;
	if (!accountId) {
		return c.ok(result.output, {
			cta: {
				description:
					"You're logged in. Choose or create the business the CLI should use:",
				commands: [
					{
						command: "quickstart",
						description: "Choose or create a business account",
					},
				],
			},
		});
	}

	return c.ok(result.output, {
		cta: {
			description: result.existed
				? "Updated and switched to this profile:"
				: "You're logged in:",
			commands: [
				{ command: "auth status", description: "Show the active identity" },
				{
					command: "quickstart",
					description: "Choose or create a business account",
				},
			],
		},
	});
}

type LogoutAdapterCtx = {
	options: { profile?: string | undefined; all?: boolean | undefined };
	ok: (data: LogoutOutput, meta?: { cta?: CtaInput | undefined }) => never;
	error: (options: ErrorInput) => never;
};

export function logoutAdapter(c: LogoutAdapterCtx): never {
	if (c.options.all) {
		if (c.options.profile) {
			return c.error({
				code: "CONFLICTING_OPTIONS",
				message:
					"--all removes every profile — don't combine it with --profile.",
				retryable: true,
			});
		}
		const removed = removeAllProfiles();
		if (removed.length === 0) {
			return c.error({
				code: "NO_PROFILES",
				message: "No profiles to remove.",
				retryable: false,
				cta: {
					description: "Log in first:",
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			});
		}
		return c.ok(
			{ removed, newActive: null },
			{
				cta: {
					description: `Removed ${removed.length} profile${removed.length === 1 ? "" : "s"}. To authenticate again:`,
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			},
		);
	}

	const target = c.options.profile ?? getActiveProfile()?.name;
	if (!target) {
		return c.error({
			code: "NO_ACTIVE_PROFILE",
			message: "No active profile to log out from.",
			retryable: false,
			cta: {
				description: "Log in first:",
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}
	const { removed, newActive } = removeProfile(target);
	if (!removed) {
		return c.error({
			code: "PROFILE_NOT_FOUND",
			message: `No profile named "${target}".`,
			retryable: false,
		});
	}
	return c.ok(
		{ removed: [target], newActive },
		newActive
			? {
					cta: {
						description: `Now using "${newActive}".`,
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				}
			: undefined,
	);
}

// --- The `auth` command group ---
export function buildAuthGroup() {
	const auth = Cli.create("auth", {
		description: "Manage Whop authentication",
	});

	auth.command("login", {
		description: "Log in to Whop",
		aliases: ["add"],
		options: loginOptions,
		env: loginEnv,
		output: loginOutput,
		examples: loginExamples,
		hint: loginHint,
		run: (c) => loginAdapter(c),
	});

	auth.command("logout", {
		description: "Log out — remove a saved profile, or all with --all",
		aliases: ["remove", "rm"],
		options: logoutOptions,
		output: logoutOutput,
		examples: [
			{ description: "Remove the active profile" },
			{
				options: { all: true },
				description: "Remove every profile (full reset)",
			},
		],
		run: (c) => logoutAdapter(c),
	});

	auth.command("switch", {
		description: "Switch the active profile",
		args: switchArgs,
		output: switchOutput,
		run: async (c) => {
			const profiles = listProfiles();
			if (profiles.length === 0) {
				return c.error({
					code: "NO_PROFILES",
					message: "No profiles yet — log in first.",
					retryable: true,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}

			let name = c.args.name;
			if (!name) {
				if (c.agent) {
					return c.error({
						code: "PROFILE_REQUIRED",
						message: `Specify a profile name. Available: ${profiles.map((p) => p.name).join(", ")}.`,
						retryable: true,
					});
				}
				try {
					name = await selectProfile(profiles);
				} catch (error) {
					if (error instanceof PromptCancelledError) {
						return c.error({
							code: "CANCELLED",
							message: "Switch cancelled.",
							exitCode: 130,
						});
					}
					throw error;
				}
			}

			const profile = switchProfile(name);
			if (!profile) {
				return c.error({
					code: "PROFILE_NOT_FOUND",
					message: `No profile named "${name}". Available: ${profiles.map((p) => p.name).join(", ")}.`,
					retryable: true,
				});
			}
			return c.ok(
				{ active: profile.name },
				{
					cta: {
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				},
			);
		},
	});

	auth.command("account", {
		description:
			"List your business accounts or set the default one for the active profile",
		aliases: ["accounts"],
		args: accountArgs,
		options: accountOptions,
		output: accountOutput,
		examples: [
			{ description: "Pick the default business account interactively" },
			{
				options: { list: true },
				description: "List the business accounts you can manage",
			},
			{
				args: { id: "biz_xxx" },
				description: "Set the default business account",
			},
		],
		hint: "OAuth profiles only — API key profiles are tied to a single business account.",
		run: async (c) => {
			const profile = getActiveProfile();
			if (!profile) {
				return c.error({
					code: "NO_ACTIVE_PROFILE",
					message: "No active profile — log in first.",
					retryable: true,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}
			if (profile.method !== "oauth") {
				return c.error({
					code: "API_KEY_PROFILE",
					message:
						"API key profiles are tied to a single business account. Log in with Whop (OAuth) to switch between the businesses you manage.",
					retryable: false,
					cta: {
						description: "To use a different business account:",
						commands: [
							{
								command: "auth login",
								description: 'Choose "Log in with Whop"',
							},
						],
					},
				});
			}

			let token: string | null;
			try {
				token = await getValidAccessToken(profile);
			} catch {
				token = null;
			}
			if (!token) {
				return c.error({
					code: "SESSION_EXPIRED",
					message: "Your session is no longer valid — log in again.",
					retryable: false,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}

			const s = c.agent
				? null
				: startSpinner("Loading your business accounts…");
			let accounts: AccountChoice[];
			try {
				accounts = await listOAuthAccounts(token);
				s?.stop(
					`Found ${accounts.length} business account${accounts.length === 1 ? "" : "s"}.`,
				);
			} catch {
				s?.stop("Couldn't load your business accounts.");
				return c.error({
					code: "ACCOUNT_LOOKUP_FAILED",
					message: "Couldn't load your business accounts. Please try again.",
					retryable: true,
				});
			}
			if (accounts.length === 0) {
				return c.error({
					code: "NO_ACCOUNTS",
					message: "You don't have a business account yet.",
					retryable: false,
					cta: {
						description: "Create your first business account:",
						commands: [
							{
								command: "quickstart",
								description: "Create and select a business account",
							},
						],
					},
				});
			}

			const currentAccount =
				profile.accountId || profile.accountTitle || profile.accountRoute
					? {
							id: profile.accountId,
							title: profile.accountTitle,
							route: profile.accountRoute,
						}
					: null;

			if (c.options.list) {
				return c.ok(
					{
						profile: profile.name,
						account: currentAccount,
						accounts: accounts.map((account) => ({
							...account,
							active: account.id === profile.accountId,
						})),
					},
					{
						cta: {
							description: "Change the default business account:",
							commands: [
								{
									command: "auth account",
									args: { id: true },
									description: "Pass a business account id or route",
								},
							],
						},
					},
				);
			}

			let requested = c.args.id;
			if (!requested) {
				if (c.agent) {
					return c.error({
						code: "ACCOUNT_REQUIRED",
						message: `Specify a business account. Available: ${accounts
							.map((account) => `${account.id} (${account.title})`)
							.join(", ")}.`,
						retryable: true,
					});
				}
				try {
					requested = (await selectAccount(accounts, profile.accountId)).id;
				} catch (error) {
					if (error instanceof PromptCancelledError) {
						return c.error({
							code: "CANCELLED",
							message: "Switch cancelled.",
							exitCode: 130,
						});
					}
					throw error;
				}
			}

			const match =
				accounts.find((account) => account.id === requested) ??
				accounts.find((account) => account.route === requested);
			if (!match) {
				return c.error({
					code: "ACCOUNT_NOT_FOUND",
					message: `No business account "${requested}" for this user. Available: ${accounts
						.map((account) => `${account.id} (${account.title})`)
						.join(", ")}.`,
					retryable: true,
				});
			}

			if (!setActiveProfileAccount(match, profile.name)) {
				return c.error({
					code: "PROFILE_REMOVED",
					message: `Profile "${profile.name}" was removed while switching — log in again.`,
					retryable: false,
					cta: {
						description: "To authenticate:",
						commands: [
							{ command: "auth login", description: "Log in to Whop" },
						],
					},
				});
			}
			return c.ok(
				{
					profile: profile.name,
					account: match,
					accounts: accounts.map((account) => ({
						...account,
						active: account.id === match.id,
					})),
				},
				{
					cta: {
						description: `Now acting on behalf of ${match.title}.`,
						commands: [
							{
								command: "auth status",
								description: "Show the active identity",
							},
						],
					},
				},
			);
		},
	});

	auth.command("status", {
		description: "Show the active identity (whoami)",
		aliases: ["whoami"],
		output: statusOutput,
		run: async (c) => {
			const profile = getActiveProfile();
			if (!profile) {
				return c.ok(
					{
						loggedIn: false,
						profile: null,
						method: null,
						identity: null,
						account: null,
						hasSecret: false,
					},
					{
						cta: {
							description: "To authenticate:",
							commands: [
								{ command: "auth login", description: "Log in to Whop" },
							],
						},
					},
				);
			}
			const hasSecret = getSecret(profile.name) !== null;
			// "Logged in" means we can actually obtain a Bearer token. For oauth
			// this may refresh the access token; a revoked/expired refresh token
			// resolves to null instead of throwing, so the reported state is honest.
			let loggedIn = hasSecret;
			if (hasSecret) {
				try {
					loggedIn = (await getValidAccessToken(profile)) !== null;
				} catch {
					loggedIn = false;
				}
			}
			const account =
				profile.accountId || profile.accountTitle || profile.accountRoute
					? {
							id: profile.accountId,
							title: profile.accountTitle,
							route: profile.accountRoute,
						}
					: null;
			return c.ok(
				{
					loggedIn,
					profile: profile.name,
					method: profile.method,
					identity:
						profile.method === "oauth"
							? { id: profile.userId, email: profile.userEmail }
							: account,
					account,
					hasSecret,
				},
				loggedIn
					? undefined
					: {
							cta: {
								description: hasSecret
									? "Your session is no longer valid — log in again:"
									: "The stored secret is missing — log in again:",
								commands: [
									{ command: "auth login", description: "Log in to Whop" },
								],
							},
						},
			);
		},
	});

	auth.command("list", {
		description: "List saved authentication profiles",
		aliases: ["ls"],
		output: listOutput,
		run: () => {
			const { activeProfile, profiles } = readConfig();
			const active =
				profiles.find((p) => p.name === activeProfile)?.name ?? null;
			return { active, profiles };
		},
	});

	return auth;
}
